import { useMutation } from '@apollo/client';
import { updateChannel } from 'lane-shared/graphql/mutation';
import { getChannel, getTenantDetailsQuery } from 'graphql-queries';
import { castForUpdate } from 'lane-shared/helpers';
import { safeConvertToUUID } from 'uuid-encoding';

export interface SaveDomainsResult {
  success: boolean;
  error?: Error | unknown;
}

/**
 * Hook for updating tenant permitted domains via updateChannel mutation.
 * Domains are stored in channel.filters field.
 *
 * @param channelId - The channel ID (tenant channel ID)
 * @param propertyId - Optional property ID for refetching tenant details
 * @param tenantId - Optional tenant ID for refetching tenant details
 * @returns Object containing saveDomains function, loading state, and error state
 */
export function useTenantDomainsMutation(
  channelId: string,
  propertyId?: string,
  tenantId?: string
) {
  // Build refetch queries array
  const refetchQueries: Array<{
    query: any;
    variables: { id?: string; propertyId?: string; tenantId?: string };
  }> = [
    {
      query: getChannel,
      variables: { id: channelId },
    },
  ];

  // Add tenant details query to refetch if propertyId and tenantId are provided
  if (propertyId && tenantId) {
    refetchQueries.push({
      query: getTenantDetailsQuery,
      variables: {
        propertyId: safeConvertToUUID(propertyId),
        tenantId: safeConvertToUUID(tenantId),
      },
    });
  }

  const [updateChannelMutation, { loading, error }] = useMutation(
    updateChannel,
    {
      refetchQueries,
    }
  );

  const saveDomains = async (domains: string[]): Promise<SaveDomainsResult> => {
    // Filter empty strings and trim
    const filteredDomains = domains
      .map(domain => domain.trim())
      .filter(domain => domain !== '');

    try {
      const result = await updateChannelMutation({
        variables: {
          channel: castForUpdate({
            _id: channelId,
            filters: filteredDomains,
          }),
        },
      });

      if (result.errors && result.errors.length > 0) {
        return {
          success: false,
          error: new Error(result.errors.map(e => e.message).join('; ')),
        };
      }

      return {
        success: true,
        // Domains available via refetched channel query
      };
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));

      console.error('Failed to update tenant domains:', error);

      return {
        success: false,
        error,
      };
    }
  };

  return {
    saveDomains,
    loading,
    error,
  };
}
